if (!customElements.get('rose-beauty-quiz')) {
  customElements.define('rose-beauty-quiz', class RoseBeautyQuiz extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.storageKey = `rose-beauty-brief:${this.dataset.theme || 'draft'}:${this.id}`;
      this.form = this.querySelector('form');
      if (!this.form) return;
      const success = this.querySelector('[data-quiz-success]');
      if (success) {
        try { sessionStorage.removeItem(this.storageKey); } catch (_) {}
        success.focus({ preventScroll: true });
        return;
      }
      this.panels = [...this.querySelectorAll('[data-step]')];
      this.radios = [...this.querySelectorAll('input[data-key]')];
      this.back = this.querySelector('[data-back]');
      this.next = this.querySelector('[data-next]');
      this.error = this.querySelector('[data-error]');
      this.summary = this.querySelector('[data-summary]');
      this.coverage = this.querySelector('[data-coverage]');
      this.phone = this.querySelector('[data-phone]');
      this.channel = this.querySelector('[data-channel]');
      this.current = 0;
      this.form.noValidate = true;
      const signal = this.controller.signal;
      this.addEventListener('change', event => this.handleChange(event), { signal });
      this.next.addEventListener('click', () => this.advance(), { signal });
      this.back.addEventListener('click', () => this.show(this.current - 1, true), { signal });
      this.querySelector('[data-restart]').addEventListener('click', () => this.restart(), { signal });
      this.summary.addEventListener('click', event => {
        const button = event.target.closest('[data-edit-step]');
        if (button) this.show(Number(button.dataset.editStep), true);
      }, { signal });
      this.form.addEventListener('submit', event => this.handleSubmit(event), { signal });
      this.classList.add('is-enhanced');
      this.restore();
      this.updateCoverage();
      this.updateContactChannel();
      this.buildSummary();
      if (this.querySelector('[data-server-error]')) this.current = 5;
      this.show(this.current, false);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.initialized = false;
    }

    chosen(key) {
      return this.radios.find(input => input.dataset.key === key && input.checked);
    }

    handleChange(event) {
      if (event.target.matches('input[data-key]')) {
        this.error.textContent = '';
        this.updateCoverage();
        this.buildSummary();
        this.save();
      }
      if (event.target === this.channel) this.updateContactChannel();
    }

    updateCoverage() {
      const show = this.chosen('focus')?.dataset.value === 'complexion';
      this.coverage.hidden = !show;
      this.coverage.querySelectorAll('input').forEach(input => {
        input.disabled = !show;
        input.required = show;
      });
    }

    updateContactChannel() {
      const needsPhone = this.channel.value.includes('WhatsApp');
      this.phone.required = needsPhone;
      this.querySelector('[data-phone-required]').textContent = needsPhone ? '*' : '';
    }

    show(index, focus) {
      this.current = Math.min(5, Math.max(0, index));
      this.panels.forEach((panel, i) => { panel.hidden = i !== this.current; });
      this.back.disabled = this.current === 0;
      this.next.hidden = this.current === 5;
      this.querySelector('[data-progress-text]').textContent = `${String(this.current + 1).padStart(2, '0')} / 06`;
      const plateNumber = this.querySelector('[data-plate-number]');
      if (plateNumber) plateNumber.textContent = String(this.current + 1).padStart(2, '0');
      this.querySelector('[data-progress-bar]').style.width = `${((this.current + 1) / 6) * 100}%`;
      this.querySelector('[role="progressbar"]').setAttribute('aria-valuenow', String(this.current + 1));
      this.error.textContent = '';
      this.querySelector('[data-controls]').classList.toggle('rb-quiz__controls--contact', this.current === 5);
      const image = this.querySelector('[data-quiz-art]');
      const source = this.getAttribute(`data-step-image-${this.current}`);
      if (image && source && image.getAttribute('src') !== source) image.src = source;
      this.save();
      if (focus) {
        const heading = this.panels[this.current].querySelector('.rb-quiz__question');
        heading.focus({ preventScroll: true });
        const workspace = this.querySelector('.rb-quiz__workspace');
        if (workspace.getBoundingClientRect().top < 20) workspace.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      }
    }

    validateStep(index, focus = true) {
      const invalid = [...this.panels[index].querySelectorAll('input, select, textarea')].find(input => !input.disabled && !input.checkValidity());
      if (!invalid) return true;
      this.show(index, false);
      this.error.textContent = index < 5 ? this.dataset.requiredMessage : invalid.validationMessage;
      if (focus) {
        invalid.focus();
        if (index === 5) invalid.reportValidity();
      }
      return false;
    }

    advance() {
      if (this.validateStep(this.current)) this.show(this.current + 1, true);
    }

    buildSummary() {
      const rows = [];
      ['focus', 'tone', 'undertone', 'finish', 'mood'].forEach((key, index) => {
        const answer = this.chosen(key);
        rows.push({ label: this.panels[index].dataset.summaryLabel, value: answer?.value || this.dataset.notChosen, step: index });
      });
      if (this.chosen('focus')?.dataset.value === 'complexion') {
        const coverage = this.chosen('coverage');
        rows.splice(4, 0, { label: this.dataset.coverageLabel || 'Coverage', value: coverage?.value || this.dataset.notChosen, step: 3 });
      }
      const title = document.createElement('p');
      title.className = 'rb-quiz__summary-heading';
      title.textContent = this.dataset.summaryTitle;
      const list = document.createElement('dl');
      rows.forEach(row => {
        const wrapper = document.createElement('div');
        wrapper.className = 'rb-quiz__summary-row';
        const term = document.createElement('dt');
        term.textContent = row.label;
        const value = document.createElement('dd');
        value.textContent = row.value;
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.editStep = String(row.step);
        button.textContent = this.dataset.editLabel;
        button.setAttribute('aria-label', `${this.dataset.editLabel} ${row.label.toLowerCase()}`);
        wrapper.append(term, value, button);
        list.append(wrapper);
      });
      this.summary.replaceChildren(title, list);
      this.querySelector('[data-brief]').value = `Rose Beauty — personal makeup consultation request.\n\n${rows.map(row => `${row.label}: ${row.value}`).join('\n')}\n\nThis is a consultation request, not an order.`;
    }

    handleSubmit(event) {
      if (this.current < 5) {
        event.preventDefault();
        this.advance();
        return;
      }
      for (let i = 0; i < this.panels.length; i++) {
        if (!this.validateStep(i)) {
          event.preventDefault();
          return;
        }
      }
      this.buildSummary();
      this.save();
    }

    save() {
      const answers = {};
      this.radios.forEach(input => {
        if (input.checked && !input.disabled) answers[input.dataset.key] = input.dataset.value;
      });
      try { sessionStorage.setItem(this.storageKey, JSON.stringify({ version: 1, step: this.current, answers })); } catch (_) {}
    }

    restore() {
      try {
        const data = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (!data || data.version !== 1 || typeof data.answers !== 'object') return;
        this.radios.forEach(input => { input.checked = data.answers[input.dataset.key] === input.dataset.value; });
        this.current = Math.min(5, Math.max(0, Number(data.step) || 0));
        const keys = ['focus', 'tone', 'undertone', 'finish', 'mood'];
        for (let index = 0; index < this.current; index++) {
          if (!this.chosen(keys[index])) { this.current = index; break; }
        }
      } catch (_) {}
    }

    restart() {
      this.radios.forEach(input => { input.checked = false; });
      try { sessionStorage.removeItem(this.storageKey); } catch (_) {}
      this.updateCoverage();
      this.buildSummary();
      this.show(0, true);
    }
  });
}
